'use client'

import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import React from 'react'

import type { LucideIconName } from '~/core/ui/IconWrapper'
import IconWrapper from '~/core/ui/IconWrapper'
import { cn } from '~/core/ui/utils'

export const buttonSizeIconLoadingVariants = cva<
  Record<string, { [key: string]: string }>
>('', {
  variants: {
    size: {
      lg: 'h-[22px] w-[22px]',
      md: 'h-5 w-5',
      sm: 'h-[18px] w-[18px]',
      xs: 'h-4 w-4'
    }
  },
  defaultVariants: {
    size: 'lg'
  }
})

const buttonRootVariants = cva<Record<string, { [key: string]: string }>>(
  'rounded-xs',
  {
    variants: {
      variant: {
        lg: 'h-[42px] w-[42px]',
        md: 'h-[38px] w-[38px]',
        sm: 'h-8 w-8',
        xs: 'h-6 w-6'
      },
      disabled: {
        default: '',
        disabled: 'pointer-events-none'
      }
    },
    defaultVariants: {
      variant: 'lg',
      disabled: 'default'
    }
  }
)

export const buttonIconVariants = cva<
  Record<string, { [key: string]: string }>
>('', {
  variants: {
    variant: {
      primaryNoDisabled: '',
      primaryWithDisabled: '',
      secondaryNoDisabled: 'text-gray-500 dark:text-gray-400',
      secondaryWithDisabled: 'text-gray-400',
      'secondary-destructiveNoDisabled': '',
      'secondary-destructiveWithDisabled': '',
      tertiaryNoDisabled: 'text-gray-500 dark:text-gray-400',
      tertiaryWithDisabled: 'text-gray-400',
      'tertiary-destructiveNoDisabled': '',
      'tertiary-destructiveWithDisabled': '',
      destructiveNoDisabled: '',
      destructiveWithDisabled: ''
    }
  },
  defaultVariants: {
    variant: 'primaryNoDisabled'
  }
})

const sizeIcon = {
  lg: 22,
  md: 20,
  sm: 18,
  xs: 16
}

interface IconButtonProps {
  type?:
    | 'primary'
    | 'secondary'
    | 'secondary-destructive'
    | 'tertiary'
    | 'tertiary-destructive'
    | 'destructive'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  isLoading?: boolean
  isDisabled?: boolean
  htmlType?: 'button' | 'submit' | 'reset'
  iconMenus?: LucideIconName
  className?: string
  autoFocus?: boolean
  submitFormId?: string
  spinLoading?: string
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const IconButton = (props: IconButtonProps) => {
  const {
    type = 'primary',
    size = 'lg',
    isDisabled = false,
    isLoading = false,
    autoFocus,
    iconMenus,
    htmlType = 'button',
    submitFormId,
    onClick,
    className,
    spinLoading,
    asChild = false,
    ref
  } = props
  const baseButton = buttonRootVariants({
    variant: size,
    disabled: isDisabled == true || isLoading == true ? 'disabled' : 'default'
  })
  const sizeIconLoading = buttonSizeIconLoadingVariants({ size })

  const classNameButton = cn(
    isDisabled == false &&
      'bg-transparent focus:shadow-2xs focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900',
    type === 'primary' &&
      isDisabled == false &&
      'text-primary-400 hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-gray-700 dark:focus:bg-gray-700',
    type === 'secondary' &&
      isDisabled == false &&
      'text-gray-600 hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:bg-gray-800',
    type === 'tertiary' &&
      isDisabled == false &&
      'text-gray-400 hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 dark:focus:bg-gray-800',
    type === 'secondary-destructive' &&
      isDisabled == false &&
      'text-gray-500 hover:bg-gray-100 hover:text-red-500 focus:bg-gray-100 focus:text-red-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:bg-gray-800',
    type === 'tertiary-destructive' &&
      isDisabled == false &&
      'text-gray-400 hover:bg-gray-100 hover:text-red-500 focus:bg-gray-100 focus:text-red-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:focus:bg-gray-800',
    type === 'destructive' &&
      isDisabled == false &&
      'text-red-500 hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-gray-800 dark:focus:bg-gray-800',
    type === 'destructive' &&
      isDisabled === false &&
      'hover:bg-gray-100 focus:bg-gray-100',
    type === 'primary' &&
      isDisabled == true &&
      'text-primary-200 dark:text-primary-800',
    [
      'secondary',
      'tertiary',
      'secondary-destructive',
      'tertiary-destructive'
    ].includes(type || 'secondary') &&
      isDisabled == true &&
      'text-gray-400 dark:text-gray-700',
    type === 'destructive' &&
      isDisabled == true &&
      'text-red-300 dark:text-red-700'
  )

  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      autoFocus={autoFocus}
      ref={ref}
      {...(!asChild && { type: htmlType })}
      {...(!asChild && { form: submitFormId })}
      onClick={(event) => {
        if (onClick && !isDisabled) {
          onClick(event)
        }
      }}
      className={cn(
        'flex items-center justify-center',
        baseButton,
        classNameButton,
        className
      )}>
      {isLoading ? (
        <div className="justify-cente flex items-center">
          <div>
            <svg
              aria-hidden="true"
              className={cn(
                'animate-spin fill-white',
                sizeIconLoading,
                spinLoading
              )}
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center">
          <IconWrapper
            className={cn(
              buttonIconVariants({
                variant: `${type || 'primary'}${isDisabled ? 'WithDisabled' : 'NoDisabled'}`
              })
            )}
            name={iconMenus}
            size={sizeIcon[size || 'lg']}
          />
        </div>
      )}
    </Comp>
  )
}

IconButton.displayName = 'IconButton'

export { IconButton }
export type { IconButtonProps }
